import {
  CITY_GUIDE_IMAGE_SLOTS,
  CITY_PAGE_PREVIEW_COUNT,
  type CityGuideImageSlot,
  type CityPage,
  type CityPageAttractionCard,
  CityPageCategoryMix,
  CityPageCategoryShare,
  CityPageEssentialInfo,
  CityPageInstagramPost,
  CityPageLive,
  type CityPageSection,
  CityPageSectionImage,
  CityPageVenue,
  DatasetCity,
  IDENTITY_CELLS,
  type IdentityCell,
  isSectionTypeId,
  LandingCity,
  LandingData,
  UnsubscribeInfo,
} from "@noko-city/shared";

// Runtime decoders for the public landing endpoints. The payload interfaces live
// in @noko-city/shared as types only, so these guards re-check every field a
// response promises — nested objects, arrays, nullability and literal unions
// included — before a successful response is handed on as a typed value. They
// narrow in place: the caller keeps the original object (extra fields included).
//
// Image URLs are the one field that is repaired rather than rejected: a cover or
// illustration whose URL is not one of the shapes this site issues is dropped
// (the cover becomes a link-only card, the illustration becomes text-only) so a
// single bad image can never blank a whole guide. Nothing illegal is ever passed
// on as a string.

/** GET /api/landing/cities envelope. */
export interface CitiesResponse {
  cities: DatasetCity[];
}

function isArrayOf<T>(value: unknown, guard: (item: unknown) => item is T): value is T[] {
  return Array.isArray(value) && value.every(guard);
}

/** A field the API serializes as `T | null`, i.e. present but possibly null. */
function isNullable(value: unknown, type: "string" | "number"): boolean {
  return value === null || typeof value === type;
}

/**
 * One arriving leg: four coordinates and two instants, all of them required, plus the labelling a
 * card needs — an airport code at each end and the flight's own designator.
 *
 * The labelling is optional, each present field checked when it is there: a document baked before
 * these fields existed is still a valid document, and the card then shows the route instead of a
 * number. A leg the globe cannot place is rejected with the payload rather than drawn somewhere
 * else — the planes are decoration, but a plane at an invented position would still be a claim
 * about a real flight.
 */
function isFlightLeg(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const leg = value as Record<string, unknown>;
  const labelled =
    (leg.fromCode === undefined || typeof leg.fromCode === "string") &&
    (leg.toCode === undefined || typeof leg.toCode === "string") &&
    (leg.designator === undefined || isNullable(leg.designator, "string"));
  return (
    labelled &&
    Number.isFinite(leg.fromLat) &&
    Number.isFinite(leg.fromLng) &&
    Number.isFinite(leg.toLat) &&
    Number.isFinite(leg.toLng) &&
    typeof leg.takeoffAt === "string" &&
    typeof leg.landingAt === "string"
  );
}

function isLandingCity(value: unknown): value is LandingCity {
  if (typeof value !== "object" || value === null) return false;
  if (!("name" in value) || typeof value.name !== "string") return false;
  if (!("timezone" in value) || typeof value.timezone !== "string") return false;
  if (!("instagramHandle" in value) || !isNullable(value.instagramHandle, "string")) return false;
  if (!("eventCount" in value) || typeof value.eventCount !== "number") return false;
  if (!("lat" in value) || !isNullable(value.lat, "number")) return false;
  if (!("lng" in value) || !isNullable(value.lng, "number")) return false;
  if (!("slug" in value) || !isNullable(value.slug, "string")) return false;
  if (!("cityPagePublished" in value) || typeof value.cityPagePublished !== "boolean") return false;
  // Optional, so a document served before the directory announced cities still validates:
  // absent and null both mean "not announced".
  if ("comingSoon" in value && value.comingSoon !== null && typeof value.comingSoon !== "boolean") {
    return false;
  }
  // Optional like `flightLegs`, and checked the same way: a direction object that is only
  // half there would be an invented figure, so it is refused rather than drawn.
  if (
    "arrivals" in value &&
    value.arrivals !== null &&
    !isCityLiveFlightDirection(value.arrivals)
  ) {
    return false;
  }
  // Optional, so a home document baked before the globe drew planes still validates: absent,
  // null and an empty list all mean nothing to draw.
  if (!("flightLegs" in value) || value.flightLegs === null) return true;
  return Array.isArray(value.flightLegs) && value.flightLegs.every(isFlightLeg);
}

export function isLandingData(value: unknown): value is LandingData {
  if (typeof value !== "object" || value === null) return false;
  if (!("generatedAt" in value) || typeof value.generatedAt !== "string") return false;
  if (!("cities" in value) || !isArrayOf(value.cities, isLandingCity)) return false;
  if (!("viewerCountry" in value)) return true;
  // Optional GeoIP hint: absent, null and a string are all valid.
  return value.viewerCountry === null || typeof value.viewerCountry === "string";
}

function isCityPageCategoryShare(value: unknown): value is CityPageCategoryShare {
  if (typeof value !== "object" || value === null) return false;
  if (!("slug" in value) || typeof value.slug !== "string") return false;
  if (!("name" in value) || typeof value.name !== "string") return false;
  if (!("icon" in value) || typeof value.icon !== "string") return false;
  if (!("count" in value) || typeof value.count !== "number") return false;
  return "percent" in value && typeof value.percent === "number";
}

function isCityPageCategoryMix(value: unknown): value is CityPageCategoryMix {
  if (typeof value !== "object" || value === null) return false;
  if (!("windowDays" in value) || typeof value.windowDays !== "number") return false;
  if (!("total" in value) || typeof value.total !== "number") return false;
  return "categories" in value && isArrayOf(value.categories, isCityPageCategoryShare);
}

/**
 * The media CDN host this build was configured with — no scheme, no trailing
 * slash. Optional: an unset host (a local build with no CDN) simply drops the
 * absolute forms below and keeps the two same-origin ones, which is exactly what
 * that deployment serves.
 */
const MEDIA_HOST = (import.meta.env.VITE_MEDIA_HOST ?? "")
  .trim()
  .replace(/^https?:\/\//i, "")
  .replace(/\/+$/, "");

/** Canonical shapes only: lowercase sha256, the three published extensions. */
const SHA256 = "[0-9a-f]{64}";
const IMAGE_EXT = "(?:png|jpg|webp)";
/**
 * The public illustration namespaces, current first. The legacy
 * `public-images/v1/` path is still anonymous on the platform by design — the
 * backend's objectCacheControl keeps it public precisely because prerendered
 * landing HTML names it — and live guide payloads carry it, so both must decode
 * here or every published illustration is dropped on hydration.
 */
const PUBLIC_IMAGE_NAMESPACES: ReadonlyArray<string> = ["public/v1", "public-images/v1"];
/** A guide page's own slug: the shape the CDN viewer function treats as canonical. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The image URL shapes this site issues, compiled once per payload. */
interface GuideUrlMatchers {
  /** The city's own first-page cover: the static file written beside the page. */
  cover: RegExp;
  /** A permanent content-addressed illustration. */
  sectionImage: RegExp;
}

/**
 * Every form is anchored, its host is a literal and its slug is escaped, so a
 * credential (`user@host`), a query string or fragment, a doubled slash, `..`
 * and the internal `__branded` / `__resized` / `instagram-archive` prefixes are
 * all structurally unmatchable — there is no catch-all branch for any of them to
 * fall through. Both public illustration namespaces are accepted; see
 * `PUBLIC_IMAGE_NAMESPACES` for why the legacy one cannot be dropped here.
 */
function guideUrlMatchers(slug: string): GuideUrlMatchers {
  const host = MEDIA_HOST ? `https://${escapeRegExp(MEDIA_HOST)}` : null;
  const escapedSlug = escapeRegExp(slug);
  // The slots, listed rather than written as a range so this pattern cannot fall
  // behind the shared count: a matcher capped at three is what silently rejected
  // the fourth and fifth covers as invalid.
  const slots = Array.from({ length: CITY_PAGE_PREVIEW_COUNT }, (_, index) => index + 1).join("|");
  return {
    // The covers are plain files on this very origin, one full-width file per
    // published slot plus the reduced variants a `srcset` names — the same paths
    // the backend writes `live/<slug>/post-N.jpg` and `post-N-<width>.jpg` from,
    // with the slot number taken from the shared count. The width group is open
    // on purpose: the widths are a publishing decision, while the slot, slug and
    // extension are the parts this site must never accept a foreign value for.
    //
    // `png` is the format every document baked before 2026-09-21 embedded, and
    // those covers are still in the bucket: this decoder ships with the bundle
    // that hydrates them, so refusing the old extension would blank the artwork
    // of every guide until its own re-bake. Both extensions are this site's own
    // issue — the variant suffix and the slug are still pinned.
    cover: new RegExp(`^/live/${escapedSlug}/post-(?:${slots})(?:-[1-9]\\d*)?\\.(?:jpg|png)$`),
    // Each alternative is anchored on BOTH ends: without the anchors
    // `RegExp.test` is a substring search, so a query string, a longer path
    // sharing the prefix or a form embedded in a foreign URL would all match.
    sectionImage: new RegExp(
      PUBLIC_IMAGE_NAMESPACES.flatMap((namespace) => [
        `/media/${namespace}/${SHA256}\\.${IMAGE_EXT}`,
        ...(host ? [`${host}/${namespace}/${SHA256}\\.${IMAGE_EXT}`] : []),
      ])
        .map((form) => `^${form}$`)
        .join("|"),
    ),
  };
}

/**
 * True when `url` is one of `slug`'s own cover files — the shape a guide payload may name, exposed so
 * the home globe's reader of a guide's generated artifacts judges a cover by this same rule instead of
 * compiling a second one beside it.
 */
export function isCityCoverPath(slug: string, url: unknown): boolean {
  return typeof url === "string" && guideUrlMatchers(slug).cover.test(url);
}

function isCityPageSectionImage(
  value: unknown,
  matchers: GuideUrlMatchers,
): value is CityPageSectionImage {
  if (typeof value !== "object" || value === null) return false;
  if (!("src" in value) || typeof value.src !== "string") return false;
  if (!matchers.sectionImage.test(value.src)) return false;
  if (!("alt" in value) || typeof value.alt !== "string" || value.alt.trim() === "") return false;
  if (!("width" in value) || !Number.isInteger(value.width) || (value.width as number) < 1) {
    return false;
  }
  return "height" in value && Number.isInteger(value.height) && (value.height as number) >= 1;
}

/**
 * Keep only the slots carrying a legal illustration. Absent, misshapen and
 * unusable entries all leave the payload text-only for that slot — never a
 * placeholder, never another city's picture, never a raw string.
 */
function sanitizeSectionImages(
  value: object & { sectionImages?: unknown },
  matchers: GuideUrlMatchers,
): void {
  const raw = value.sectionImages;
  if (raw === undefined) return;
  const kept: Partial<Record<CityGuideImageSlot, CityPageSectionImage>> = {};
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    const slots = raw as Record<string, unknown>;
    for (const slot of CITY_GUIDE_IMAGE_SLOTS) {
      const image = slots[slot];
      if (isCityPageSectionImage(image, matchers)) kept[slot] = image;
    }
  }
  if (CITY_GUIDE_IMAGE_SLOTS.some((slot) => kept[slot] !== undefined)) value.sectionImages = kept;
  else delete value.sectionImages;
}

/**
 * One resolved section, as the payload carries it: the id the renderer looks a
 * component up by, the heading it prints, its settings and whether it is drawn.
 */
function isCityPageSection(value: unknown): value is CityPageSection {
  if (typeof value !== "object" || value === null) return false;
  if (!("id" in value) || !isSectionTypeId(value.id)) return false;
  if (!("heading" in value) || typeof value.heading !== "string") return false;
  if (!("rendered" in value) || typeof value.rendered !== "boolean") return false;
  if ("icon" in value && typeof value.icon !== "string") return false;
  if ("limit" in value && (!Number.isInteger(value.limit) || (value.limit as number) < 1)) {
    return false;
  }
  if ("cells" in value && !isArrayOf(value.cells, isIdentityCell)) return false;
  if (
    "hiddenReason" in value &&
    value.hiddenReason !== "disabled" &&
    value.hiddenReason !== "no-data"
  ) {
    return false;
  }
  return true;
}

/** One identity cell: the closed vocabulary the header draws from. */
function isIdentityCell(value: unknown): value is IdentityCell {
  return typeof value === "string" && (IDENTITY_CELLS as readonly string[]).includes(value);
}

/**
 * Keep the guide's composition only when it is well formed. A section list that
 * is misshapen, or names a section type this build does not know, is dropped
 * rather than rejected: the renderer's fallback is the registry's own composition,
 * which is the guide the payload would have described, so a stale chunk meeting a
 * newer payload loses the author's order and not the page.
 */
function sanitizeSections(value: object & { sections?: unknown }): void {
  const raw = value.sections;
  if (raw === undefined) return;
  if (Array.isArray(raw) && raw.every(isCityPageSection)) return;
  delete value.sections;
}

/** Canonical Instagram destinations: a post permalink, or the profile fallback. */
const INSTAGRAM_POST_URL = /^https:\/\/www\.instagram\.com\/(?:p|reel)\/[A-Za-z0-9_-]{1,64}\/$/;
const INSTAGRAM_PROFILE_URL = /^https:\/\/www\.instagram\.com\/[A-Za-z0-9._]{1,30}\/$/;

/** One `srcset` entry: a path with no whitespace, then its width in pixels. */
const SRCSET_ENTRY = /^(\S+) ([1-9]\d*)w$/;

/**
 * The `srcset` a guide cover may carry, checked against the cover it belongs to.
 *
 * Repaired rather than rejected, like `imageUrl`: a srcset this site would not
 * have written is dropped and the card shows its full-width cover, which
 * `imageUrl` already vouches for — a bad optimisation must never blank a guide
 * nor a card. Returning null is also the honest answer for a payload baked
 * before the field existed, and for a card with no cover to reduce.
 *
 * Every entry must be a cover of THIS slug and slot, the widths must strictly
 * increase, and the full-width file must be the last one — so the browser always
 * has a fallback it can already see, and no entry can smuggle in a foreign URL.
 */
function decodeImageSrcSet(
  value: unknown,
  matchers: GuideUrlMatchers,
  cover: string | null,
): string | null {
  if (cover === null || typeof value !== "string") return null;
  // The cover minus its extension: every variant of one slot shares this prefix,
  // whatever format the cover itself is in.
  const base = cover.replace(/\.[a-z0-9]+$/, "");
  const entries = value.split(", ");
  let previousWidth = 0;
  for (const [index, entry] of entries.entries()) {
    const matched = SRCSET_ENTRY.exec(entry);
    if (matched === null) return null;
    const [, path, width] = matched;
    if (!matchers.cover.test(path) || !path.startsWith(base)) return null;
    // Exactly one entry is the full-width file, and it is the last: earlier
    // entries are the reduced variants, which is what makes the widths a
    // smallest-first list the browser can choose from.
    const isFull = path === cover;
    if (isFull !== (index === entries.length - 1)) return null;
    if (Number(width) <= previousWidth) return null;
    previousWidth = Number(width);
  }
  return entries.length > 0 ? value : null;
}

/**
 * A card's `srcset`: a reduced copy of the same illustration its `image` names.
 *
 * Repaired rather than rejected, like the rest of this file's image fields — a
 * srcset this site would not have written is dropped and the card keeps the full
 * copy `image` already vouches for, which is also the honest answer for a payload
 * baked before the field existed. Every entry must be one of our own illustration
 * URLs, the widths must strictly increase, and the last entry must be `image`
 * itself. That each entry is another size of the *same* illustration is the
 * publisher's contract rather than something a URL can prove: one reduced copy is
 * a new object under the same namespace with its own digest, so the decoder pins
 * the namespace, the shape and the widths, and trusts the object it was handed.
 */
function decodeIllustrationSrcSet(
  value: unknown,
  matchers: GuideUrlMatchers,
  primary: string | null,
): string | null {
  if (primary === null || typeof value !== "string") return null;
  const entries = value.split(", ");
  let previousWidth = 0;
  for (const [index, entry] of entries.entries()) {
    const matched = SRCSET_ENTRY.exec(entry);
    if (matched === null) return null;
    const [, path, width] = matched;
    if (!matchers.sectionImage.test(path)) return null;
    if ((path === primary) !== (index === entries.length - 1)) return null;
    if (Number(width) <= previousWidth) return null;
    previousWidth = Number(width);
  }
  return entries.length > 0 ? value : null;
}

function isCityPageInstagramPost(
  value: unknown,
  matchers: GuideUrlMatchers,
): value is CityPageInstagramPost {
  if (typeof value !== "object" || value === null) return false;
  if (!("id" in value) || typeof value.id !== "string") return false;
  if (!("imageUrl" in value)) return false;
  const card = value as { imageUrl: unknown; imageSrcSet?: unknown };
  // Repair, not reject: the card is above all a link to Instagram, so a cover
  // URL this site never issued degrades it to the link-only card instead of
  // throwing away the whole guide.
  const cover =
    typeof card.imageUrl === "string" && matchers.cover.test(card.imageUrl) ? card.imageUrl : null;
  card.imageUrl = cover;
  // Read after the repair above: a card whose cover was just dropped has no
  // widths left to offer, whatever its srcset claimed.
  card.imageSrcSet = decodeImageSrcSet(card.imageSrcSet, matchers, cover);
  if (!("instagramUrl" in value) || typeof value.instagramUrl !== "string") return false;
  if (
    !INSTAGRAM_POST_URL.test(value.instagramUrl) &&
    !INSTAGRAM_PROFILE_URL.test(value.instagramUrl)
  ) {
    return false;
  }
  if (!("pageCount" in value)) return false;
  if (value.pageCount !== null) {
    if (!Number.isInteger(value.pageCount) || (value.pageCount as number) < 1) return false;
  }
  return "isCarousel" in value && typeof value.isCarousel === "boolean";
}

/**
 * One card of the guide's experiences section, the city's curated pool resolved
 * into place cards at read time. The image is repaired, not the card: a card
 * carrying a picture this site never issued keeps its name and blurb and loses only
 * the picture — a single foreign URL must never blank a whole guide.
 */
function isCityPageAttractionCard(
  value: unknown,
  matchers: GuideUrlMatchers,
): value is CityPageAttractionCard {
  if (typeof value !== "object" || value === null) return false;
  if (!("name" in value) || typeof value.name !== "string") return false;
  if (!("blurb" in value) || !isNullable(value.blurb, "string")) return false;
  if (!("blurbSource" in value) || !isNullable(value.blurbSource, "string")) return false;
  if (!("image" in value)) return false;
  const card = value as { image: unknown; imageSrcSet?: unknown };
  const image =
    typeof card.image === "string" && matchers.sectionImage.test(card.image) ? card.image : null;
  card.image = image;
  // Read after the repair above: a card whose picture was just dropped has no
  // widths left to offer, whatever its srcset claimed.
  card.imageSrcSet = decodeIllustrationSrcSet(card.imageSrcSet, matchers, image);
  return true;
}

function isCityPageVenue(value: unknown): value is CityPageVenue {
  if (typeof value !== "object" || value === null) return false;
  if (!("name" in value) || typeof value.name !== "string") return false;
  if (!("neighborhood" in value) || !isNullable(value.neighborhood, "string")) return false;
  if (!("website" in value) || !isNullable(value.website, "string")) return false;
  if (!("instagramHandle" in value) || !isNullable(value.instagramHandle, "string")) return false;
  if (!("phone" in value) || !isNullable(value.phone, "string")) return false;
  if (!("rating" in value) || !isNullable(value.rating, "number")) return false;
  return "mapsUrl" in value && isNullable(value.mapsUrl, "string");
}

function isCityPageEssentialInfo(value: unknown): value is CityPageEssentialInfo {
  if (typeof value !== "object" || value === null) return false;
  if (!("emergency" in value)) return false;
  const emergency = value.emergency;
  if (typeof emergency !== "object" || emergency === null) return false;
  if (!("police" in emergency) || typeof emergency.police !== "string") return false;
  if (!("fire" in emergency) || typeof emergency.fire !== "string") return false;
  if (!("medical" in emergency) || typeof emergency.medical !== "string") return false;
  if (!("banks" in value) || typeof value.banks !== "string") return false;
  if (!("gettingAround" in value) || typeof value.gettingAround !== "string") return false;
  if (!("foodDrink" in value) || typeof value.foodDrink !== "string") return false;
  return "mustTry" in value && typeof value.mustTry === "string";
}

/**
 * The live header block: each half is a reading or null, and a malformed reading
 * is rejected instead of rendered — the guide would otherwise print a figure no
 * feed ever published.
 */
function isCityPageLive(value: unknown): value is CityPageLive {
  if (typeof value !== "object" || value === null) return false;
  if (!("weather" in value)) return false;
  const weather = value.weather;
  if (weather !== null) {
    if (typeof weather !== "object" || weather === null) return false;
    if (!("minC" in weather) || typeof weather.minC !== "number") return false;
    if (!("maxC" in weather) || typeof weather.maxC !== "number") return false;
    if (!("observedAt" in weather) || typeof weather.observedAt !== "string") return false;
    // The header's current reading. A page baked before the guide drew it has
    // neither field and renders the range; present with the wrong shape is not.
    if ("temperatureC" in weather && typeof weather.temperatureC !== "number") return false;
    if ("symbolCode" in weather && typeof weather.symbolCode !== "string") return false;
  }
  if (!("currency" in value)) return false;
  const currency = value.currency;
  if (currency !== null) {
    if (typeof currency !== "object" || currency === null) return false;
    if (!("code" in currency) || typeof currency.code !== "string") return false;
    if (!("ratePerEur" in currency) || typeof currency.ratePerEur !== "number") return false;
    if (!("rateDate" in currency) || typeof currency.rateDate !== "string") return false;
  }
  // The two later readings are optional because a payload baked before they
  // existed is still a valid page: absent renders the section without them, and a
  // present-but-malformed reading is rejected so the guide never prints a figure
  // the data does not support.
  if (
    "costOfLiving" in value &&
    value.costOfLiving !== null &&
    !isCityLiveCost(value.costOfLiving)
  ) {
    return false;
  }
  if ("flights" in value && value.flights !== null && !isCityLiveFlights(value.flights)) {
    return false;
  }
  return true;
}

/** One direction's traffic: two finite counts, never a fraction. */
function isCityLiveFlightDirection(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  if (!("flights" in value) || typeof value.flights !== "number") return false;
  if (!Number.isFinite(value.flights)) return false;
  return "passengers" in value && typeof value.passengers === "number";
}

function isCityLiveFlights(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  if (!("date" in value) || typeof value.date !== "string") return false;
  return (
    "arrivals" in value &&
    isCityLiveFlightDirection(value.arrivals) &&
    "departures" in value &&
    isCityLiveFlightDirection(value.departures)
  );
}

function isCityLiveCost(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  if (!("currency" in value) || typeof value.currency !== "string") return false;
  if (!("scrapedDate" in value) || typeof value.scrapedDate !== "string") return false;
  if (!("sourceDate" in value) || !isNullable(value.sourceDate, "string")) return false;
  if (!("stale" in value) || typeof value.stale !== "boolean") return false;
  if (!("items" in value) || !Array.isArray(value.items)) return false;
  for (const item of value.items) {
    if (typeof item !== "object" || item === null) return false;
    if (!("key" in item) || typeof item.key !== "string") return false;
    if (!("label" in item) || typeof item.label !== "string") return false;
    if (!("itemGroup" in item) || typeof item.itemGroup !== "string") return false;
    if (!("priceLocal" in item) || typeof item.priceLocal !== "number") return false;
    if (!("priceEur" in item) || !isNullable(item.priceEur, "number")) return false;
  }
  return true;
}

export function isCityPage(value: unknown): value is CityPage {
  if (typeof value !== "object" || value === null) return false;
  if (!("slug" in value) || typeof value.slug !== "string") return false;
  const slug = value.slug;
  // The slug keys the cover pattern and the page URL alike, so it must be the
  // canonical shape before either is derived from it.
  if (!SLUG.test(slug)) return false;
  const matchers = guideUrlMatchers(slug);
  if (!("name" in value) || typeof value.name !== "string") return false;
  if (!("country" in value) || typeof value.country !== "string") return false;
  if (!("timezone" in value) || typeof value.timezone !== "string") return false;
  if (!("instagramHandle" in value) || !isNullable(value.instagramHandle, "string")) return false;
  if (!("lat" in value) || !isNullable(value.lat, "number")) return false;
  if (!("lng" in value) || !isNullable(value.lng, "number")) return false;
  if (!("venues" in value) || !isArrayOf(value.venues, isCityPageVenue)) return false;
  if (!("essentialInfo" in value) || !isCityPageEssentialInfo(value.essentialInfo)) return false;
  // Fields added after the first guide release: absent (a payload built against
  // an older API) is valid; present with the wrong shape is not.
  if ("live" in value && value.live !== null && !isCityPageLive(value.live)) return false;
  if (
    "categoryMix" in value &&
    value.categoryMix !== null &&
    !isCityPageCategoryMix(value.categoryMix)
  ) {
    return false;
  }
  if (
    "instagramPosts" in value &&
    !isArrayOf(value.instagramPosts, (post) => isCityPageInstagramPost(post, matchers))
  ) {
    return false;
  }
  if ("history" in value && typeof value.history !== "string") return false;
  if (
    "attractions" in value &&
    !isArrayOf(value.attractions, (card) => isCityPageAttractionCard(card, matchers))
  ) {
    return false;
  }
  sanitizeSectionImages(value, matchers);
  sanitizeSections(value);
  return true;
}

function isDatasetCity(value: unknown): value is DatasetCity {
  if (typeof value !== "object" || value === null) return false;
  if (!("name" in value) || typeof value.name !== "string") return false;
  return "state" in value && typeof value.state === "string";
}

export function isCitiesResponse(value: unknown): value is CitiesResponse {
  if (typeof value !== "object" || value === null) return false;
  return "cities" in value && isArrayOf(value.cities, isDatasetCity);
}

export function isUnsubscribeInfo(value: unknown): value is UnsubscribeInfo {
  if (typeof value !== "object" || value === null) return false;
  if (!("email" in value) || typeof value.email !== "string") return false;
  if (!("category" in value)) return false;
  if (value.category !== "post_status" && value.category !== "city_notification") return false;
  return "unsubscribed" in value && typeof value.unsubscribed === "boolean";
}

/** The error envelope of a non-2xx landing response. */
export function errorMessageOf(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  if (!("message" in value)) return undefined;
  const message = value.message;
  const first: unknown = Array.isArray(message) ? message[0] : message;
  return typeof first === "string" && first ? first : undefined;
}
